import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  Calculator,
  PoundSterling,
} from "lucide-react";

const PLANS = [
  {
    name: "Essential",
    price: "£3,500",
    perWhat: "per transition",
    fit: "Senior IC and Manager exits.",
    bullets: [
      "Structured exit interview (90 min)",
      "Knowledge book — up to 30 critical items",
      "8-week onboarding plan for replacement",
      "ROI report sent to your CFO",
      "Email support, 1 business day SLA",
    ],
    cta: "Start an Essential transition",
    badge: null as string | null,
  },
  {
    name: "Standard",
    price: "£5,500",
    perWhat: "per transition",
    fit: "Director-level and revenue-owning roles.",
    bullets: [
      "Everything in Essential",
      "Two consultant-facilitated sessions",
      "Knowledge book — up to 60 items, with relationship and decision logs",
      "12-week onboarding plan + week-by-week productivity tracking",
      "Stakeholder shadow plan (top 5 accounts or systems)",
      "Slack/Teams support, same-business-day SLA",
    ],
    cta: "Start a Standard transition",
    badge: "Most chosen",
  },
  {
    name: "Premium",
    price: "£9,000",
    perWhat: "per transition",
    fit: "VP / C-level and founder departures.",
    bullets: [
      "Everything in Standard",
      "Three sessions including board, investor and key-customer briefing prep",
      "Unlimited knowledge units, full decision archive",
      "Replacement coaching for first 30 days post-start",
      "Confidential exit debrief delivered to CEO",
      "Dedicated consultant lead",
    ],
    cta: "Talk about a Premium transition",
    badge: null,
  },
];

const COST_OF_DOING_NOTHING = [
  { label: "Months of lost productivity", value: "6–9", note: "Industry average for senior re-ramp." },
  { label: "Salary value lost per exit", value: "£42k–£75k", note: "On £85k–£150k base, 50% productivity gap × 6 months." },
  { label: "Revenue / renewals at risk", value: "£100k+", note: "When the leaver owned customer relationships." },
  { label: "Time HR & manager spend reactively", value: "40+ hrs", note: "Hunting context the leaver could have transferred in 90 minutes." },
];

const FOOTNOTES = [
  "Productivity ramp benchmark: SHRM 2019 'Retention Report' and Gartner HR Research 2023 — senior individual contributors average 6 months to full productivity; first-line managers 9 months.",
  "Fees shown are list price for UK-based engagements. Volume agreements (5+ transitions) and multi-year retainers are quoted separately. VAT not included.",
  "Cost of doing nothing figures are illustrative for a single senior departure on £100k base salary; your ROI report is built from your actual headcount and salary band.",
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-30 backdrop-blur bg-background/85">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm leading-none">R</span>
            </div>
            <span className="font-semibold">Relay</span>
          </Link>
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ChevronLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </header>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 text-primary text-xs font-medium mb-4 uppercase tracking-wide">
              Pricing
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-5">Priced per transition. No per-seat fees.</h1>
            <p className="text-lg text-muted-foreground">
              You only pay when a senior employee actually leaves. We scope the engagement to the seniority of the role
              and the risk it carries.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => {
              const featured = plan.badge !== null;
              return (
                <div
                  key={plan.name}
                  className={`rounded-2xl border p-8 relative flex flex-col ${
                    featured
                      ? "border-primary bg-primary/[0.03] shadow-lg shadow-primary/10"
                      : "border-border bg-card"
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <h3 className="text-lg font-bold">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1.5">
                    <span className="text-4xl font-black">{plan.price}</span>
                    <span className="text-muted-foreground text-sm">{plan.perWhat}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{plan.fit}</p>

                  <ul className="space-y-2.5 flex-1 my-7">
                    {plan.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary/70 flex-shrink-0 mt-0.5" />
                        <span className={featured ? "text-foreground" : "text-muted-foreground"}>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <Link href="/dashboard">
                    <Button className="w-full" variant={featured ? "default" : "outline"}>
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-10 max-w-2xl mx-auto">
            All plans are paid on completion of the Capture phase. If you are unsatisfied with the knowledge book at the
            end of Capture, you pay nothing.
          </p>
        </div>
      </section>

      {/* Cost of doing nothing */}
      <section className="py-20 bg-muted/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 text-xs font-medium mb-4 uppercase tracking-wide">
              <AlertTriangle className="h-3 w-3" /> The fourth option: do nothing
            </div>
            <h2 className="text-3xl font-bold tracking-tight mb-4">What an unmanaged senior exit actually costs.</h2>
            <p className="text-base text-muted-foreground">
              Compare any of the prices above against the typical impact of letting a senior employee walk out without a
              structured handover.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {COST_OF_DOING_NOTHING.map((item) => (
              <div key={item.label} className="rounded-xl border border-border bg-card p-5">
                <div className="text-3xl font-bold tracking-tight">{item.value}</div>
                <div className="text-sm font-medium mt-2">{item.label}</div>
                <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{item.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/calculator/advanced">
              <Button variant="default" className="gap-2">
                <Calculator className="h-4 w-4" /> Get my company's number
              </Button>
            </Link>
            <Link href="/calculator">
              <Button variant="outline" className="gap-2">
                <PoundSterling className="h-4 w-4" /> Quick estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footnotes */}
      <section className="py-14 border-b border-border">
        <div className="max-w-3xl mx-auto px-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Notes & sources</h3>
          <ol className="space-y-2 text-xs text-muted-foreground list-decimal list-inside leading-relaxed">
            {FOOTNOTES.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight mb-4">Not sure which tier you need?</h2>
          <p className="text-muted-foreground mb-8">
            Tell us the role and we will recommend the right scope. No commitment.
          </p>
          <Link href="/dashboard">
            <Button size="lg" className="gap-2">
              Book a 20-minute scoping call <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-[10px] leading-none">R</span>
            </div>
            Relay Technologies Ltd · UK-hosted · Knowledge Never Leaves.
          </div>
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Relay Technologies Ltd</p>
        </div>
      </footer>
    </div>
  );
}
